import React, {useEffect, forwardRef, useImperativeHandle} from 'react';
import {useNavigation} from '../../hooks/navigation';
import './index.css';
import {
    completeFiltersExecution, listingForCache,
    logoutCall, lookupCall
} from "../../server-calls";
import {toast} from 'react-toastify';
import StateManager from "../../redux/StateManager";
import {CACHE_CONF, TOAST_CONTAINER_ID} from "../../utils/constants";
import _ from "lodash";
import {decryptPGPMessage} from "../../business/encryption/pgp";
import {getDeviceId} from "../../cookie";
import {initializeSocket} from "../../socket";

const Navigation = forwardRef(({
                                   acknowledge = (msg) => {
                                       return true
                                   },
                                   handlerFunction, socketCallbackFunction = () => {
    }, isCallbackNeeded = () => {
        return false
    }, errorHandler = (e) => {
        console.error(e)
    }
                               }, ref) => {

    const handleSocket = async () => {
        let socket = initializeSocket(
            StateManager.getUserEmail(),
            StateManager.getToken(),
            getDeviceId()
        );
        let refreshTimeout = null;

        const resetRefreshTimer = () => {
            if (refreshTimeout) {
                clearTimeout(refreshTimeout);
            }
            refreshTimeout = setTimeout(() => {
                console.log("No new event in 1 seconds, rendering listing.");
                socketCallbackFunction()
            }, 1000);
        };

        socket.on("message", (emitData) => {
            const message = JSON.parse(emitData.body);
            handlerFunction(message).then(() => {
                if (acknowledge(message))
                    socket.emit("messageReceived", emitData.deliveryTag)
                if (isCallbackNeeded(message)) {
                    resetRefreshTimer();
                }
            }).catch((e) => {
                errorHandler(e)
                if (acknowledge(message))
                    socket.emit("messageReceived", emitData.deliveryTag)
            })
        });
    }

    const {redirect} = useNavigation();
    const logout = async () => {
        toast.promise(performLogoutOperations(), {
            pending: "Logging you out",
            success: "Log out success",
            error: "Server error occurred"
        }, {containerId: TOAST_CONTAINER_ID}).then(() => {
            redirect("/login")
        })
    }

    useImperativeHandle(ref, () => ({
        handleSocket,
    }));

    async function performLogoutOperations() {
        try {
            await logoutCall(StateManager.getToken())
            StateManager.cleanState()
            return true
        } catch (e) {
            console.error(e)
            return Promise.reject()
        }
    }

    async function startingOperations() {
        if (_.isEmpty(getDeviceId())) {
            StateManager.cleanState()
            redirect("/login")
            return
        }
        lookupCall(StateManager.getToken()).then((response) => {
            performFiltersIfAny(response)
        }).catch((e) => {
            console.error(e)
            redirect("/login")
        })
    }

    useEffect(() => {
        startingOperations()
    }, []);

    function performFiltersIfAny(data) {
        if (data.tempCount > 0) {
            performFilters(data.filters)
        } else {
            console.log("No pending filters")
        }
    }

    async function performFilters(filters) {
        filters = JSON.parse(filters)
        let body = CACHE_CONF
        body.box = "Temp"
        let lastId = null
        while (true) {
            try {
                body.lastId = lastId
                let mailData = await listingForCache(body, StateManager.getToken())
                if (_.isEmpty(mailData.mails)) {
                    break;
                }
                let mails = mailData.mails
                for (let j = 0; j < mails.length; j++) {
                    let mailObject = mails[j]

                    let [decryptedSubject, decryptedBody] = await Promise.all(
                        decryptPGPMessage(StateManager.getPrivateKey(), StateManager.getPassphrase(), mailObject.subject.replaceAll("@", "\n")),
                        decryptPGPMessage(StateManager.getPrivateKey(), StateManager.getPassphrase(), mailObject.body)
                    )

                    let filterExecContinueBody = []

                    for (let i = 0; i < filters.length; i++) {
                        let filter = filters[i]
                        if (filter.condition.field === "subject") {
                            if (filter.condition.op === "includes") {
                                if (decryptedSubject.includes(filter.condition.value)) {
                                    filterExecContinueBody.push({indexOfFilter: i, match: true})
                                } else {
                                    filterExecContinueBody.push({indexOfFilter: i, match: false})
                                }
                            } else if (filter.condition.op === "equals") {
                                if (decryptedSubject === filter.condition.value) {
                                    filterExecContinueBody.push({indexOfFilter: i, match: true})
                                } else {
                                    filterExecContinueBody.push({indexOfFilter: i, match: false})
                                }
                            } else {
                                filterExecContinueBody.push({indexOfFilter: i, match: false})
                            }
                        } else if (filter.condition.field === "body") {
                            if (filter.condition.op === "includes") {
                                if (decryptedBody.includes(filter.condition.value)) {
                                    filterExecContinueBody.push({indexOfFilter: i, match: true})
                                } else {
                                    filterExecContinueBody.push({indexOfFilter: i, match: false})
                                }
                            } else if (filter.condition.op === "equals") {
                                if (decryptedBody === filter.condition.value) {
                                    filterExecContinueBody.push({indexOfFilter: i, match: true})
                                } else {
                                    filterExecContinueBody.push({indexOfFilter: i, match: false})
                                }
                            } else {
                                filterExecContinueBody.push({indexOfFilter: i, match: false})
                            }
                        }
                    }

                    completeFiltersExecution({uid: mailObject.uid, executed: filterExecContinueBody})

                    lastId = mailObject.uid
                }
            } catch (e) {
                console.error(e)
                break
            }
        }
    }

    return (
        <div style={{borderRight: "1px solid grey", height: "100%"}} className="w-1/5 bg-gray-200">
            <ul style={{listStyleType: 'none'}} className="p-0">
                <li className="customLi mb-2 p-1" style={{cursor: "pointer"}} onClick={() => redirect("/compose")}>
                    Compose
                </li>
                <li className="customLi mb-2 p-1" style={{cursor: "pointer"}} onClick={() => redirect("/mail")}>
                    Mail
                </li>
                <li className="customLi mb-2 p-1" style={{cursor: "pointer"}} onClick={() => redirect("/domains")}>
                    Domains
                </li>
                <li className="customLi mb-2 p-1" style={{cursor: "pointer"}} onClick={() => redirect("/aliases")}>
                    Aliases
                </li>
                <li className="customLi mb-2 p-1" style={{cursor: "pointer"}} onClick={() => redirect("/filters")}>
                    Filters
                </li>
                <li className="customLi mb-2 p-1" style={{cursor: "pointer"}} onClick={() => redirect("/gmail-import")}>
                    Gmail Import
                </li>
                <li className="customLi mb-2 p-1" style={{cursor: "pointer"}}
                    onClick={() => redirect("/password-change")}>
                    Change Credentials
                </li>
                <li className="customLi mb-2 p-1" style={{cursor: "pointer"}} onClick={() => redirect("/settings")}>
                    Settings
                </li>
                <li className="customLi mb-2 p-1" style={{cursor: "pointer"}} onClick={logout}>
                    Logout
                </li>
            </ul>
        </div>
    );
});

export default Navigation;
